'use strict';
/* ================================================================
   ALPHADOG THEME — theme.js
   ================================================================ */

/* ── Dark Mode ── */
const DARK_KEY = 'ad-dark';
function applyDark(dark) {
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  localStorage.setItem(DARK_KEY, dark ? '1' : '0');
}
function initDark() {
  const stored = localStorage.getItem(DARK_KEY);
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  applyDark(stored !== null ? stored === '1' : prefersDark);
  document.querySelectorAll('[data-dark-toggle]').forEach(b => b.addEventListener('click', () => {
    applyDark(document.documentElement.getAttribute('data-theme') !== 'dark');
  }));
}

/* ── Toast ── */
function toast(msg, ms = 2600) {
  let el = document.getElementById('adToast');
  if (!el) { el = document.createElement('div'); el.id = 'adToast'; el.className = 'toast'; document.body.appendChild(el); }
  el.textContent = msg;
  el.classList.add('is-visible');
  clearTimeout(el._t);
  el._t = setTimeout(() => el.classList.remove('is-visible'), ms);
}

/* ── Money format ── */
function money(cents) { return '$' + (cents / 100).toFixed(2); }

/* ── Header scroll / transparent / logo swap ── */
function initHeader() {
  const hdr = document.getElementById('siteHeader');
  if (!hdr) return;
  const isHome = document.body.classList.contains('template-index');
  const hero = document.getElementById('heroSection');
  const canTransp = isHome && hero;
  let ticking = false;
  function update() {
    const y = window.scrollY;
    if (canTransp && y < 60) {
      hdr.classList.add('site-header--transparent');
      hdr.classList.remove('site-header--scrolled');
    } else {
      hdr.classList.remove('site-header--transparent');
      hdr.classList.add('site-header--scrolled');
    }
    hdr.classList.toggle('site-header--logo-swapped', y > 80);
    ticking = false;
  }
  if (canTransp) hdr.classList.add('site-header--transparent');
  window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
  update();
}

/* ── Overlay/drawer system ── */
const drawers = {};
function openDrawer(id) {
  Object.keys(drawers).forEach(k => { if (k !== id && drawers[k].open) closeDrawer(k); });
  const d = drawers[id];
  if (!d) return;
  d.el.classList.add('is-open');
  d.ov?.classList.add('is-open');
  d.triggerEl?.setAttribute('aria-expanded', 'true');
  document.body.style.overflow = 'hidden';
  d.open = true;
  d.onOpen?.();
}
function closeDrawer(id) {
  const d = drawers[id];
  if (!d) return;
  d.el.classList.remove('is-open');
  d.ov?.classList.remove('is-open');
  d.triggerEl?.setAttribute('aria-expanded', 'false');
  document.body.style.overflow = '';
  d.open = false;
  d.onClose?.();
}
function registerDrawer(id, { ovId, triggerId, closeId, onOpen, onClose } = {}) {
  const el = document.getElementById(id);
  if (!el) return;
  const ov = ovId ? document.getElementById(ovId) : null;
  const triggerEl = triggerId ? document.getElementById(triggerId) : null;
  drawers[id] = { el, ov, triggerEl, open: false, onOpen, onClose };
  document.getElementById(closeId)?.addEventListener('click', () => closeDrawer(id));
  ov?.addEventListener('click', () => closeDrawer(id));
  triggerEl?.addEventListener('click', () => drawers[id].open ? closeDrawer(id) : openDrawer(id));
}
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') Object.keys(drawers).forEach(k => { if (drawers[k].open) closeDrawer(k); });
});

/* ── Sidebar (Shop / 555) ── */
function initSidebars() {
  let activeSb = null;
  function openSb(id) {
    if (activeSb && activeSb !== id) closeSb(activeSb);
    document.getElementById(id)?.classList.add('is-open');
    document.getElementById(id + 'Overlay')?.classList.add('is-open');
    document.querySelector(`[data-open-sb="${id}"]`)?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    activeSb = id;
  }
  function closeSb(id) {
    const sb = document.getElementById(id);
    sb?.classList.remove('is-open');
    document.getElementById(id + 'Overlay')?.classList.remove('is-open');
    document.querySelector(`[data-open-sb="${id}"]`)?.setAttribute('aria-expanded', 'false');
    sb?.querySelectorAll('.sidebar__sub.is-open').forEach(s => s.classList.remove('is-open'));
    document.body.style.overflow = '';
    if (activeSb === id) activeSb = null;
  }
  document.querySelectorAll('[data-open-sb]').forEach(b => b.addEventListener('click', () => openSb(b.dataset.openSb)));
  document.querySelectorAll('[data-close-sb]').forEach(b => b.addEventListener('click', () => closeSb(b.dataset.closeSb)));
  document.querySelectorAll('[data-sb-overlay]').forEach(el => el.addEventListener('click', () => activeSb && closeSb(activeSb)));
  document.querySelectorAll('[data-sub-open]').forEach(b => {
    b.addEventListener('click', () => {
      const sub = document.getElementById('sub-' + b.dataset.subOpen);
      sub?.classList.toggle('is-open');
      b.setAttribute('aria-expanded', sub?.classList.contains('is-open') ? 'true' : 'false');
    });
  });
  document.querySelectorAll('[data-sub-back]').forEach(b => {
    b.addEventListener('click', () => {
      const sub = document.getElementById('sub-' + b.dataset.subBack);
      sub?.classList.remove('is-open');
      document.querySelector(`[data-sub-open="${b.dataset.subBack}"]`)?.setAttribute('aria-expanded', 'false');
    });
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && activeSb) closeSb(activeSb); });
}

/* ── Mobile Nav ── */
function initMobileNav() {
  const overlay = document.getElementById('mobileOverlay');
  const nav = document.getElementById('mobileNav');
  const open = document.getElementById('mobileNavOpen');
  const close = document.getElementById('mobileNavClose');
  function openNav() { nav?.classList.add('is-open'); overlay?.classList.add('is-open'); document.body.style.overflow = 'hidden'; open?.setAttribute('aria-expanded', 'true'); }
  function closeNav() { nav?.classList.remove('is-open'); overlay?.classList.remove('is-open'); document.body.style.overflow = ''; open?.setAttribute('aria-expanded', 'false'); }
  open?.addEventListener('click', openNav);
  close?.addEventListener('click', closeNav);
  overlay?.addEventListener('click', closeNav);
  nav?.querySelectorAll('[data-nav-toggle]').forEach(b => {
    b.addEventListener('click', () => b.closest('.mobile-nav__item')?.classList.toggle('is-open'));
  });
}

/* ── Cart ── */
let cartData = null;
function initCart() {
  registerDrawer('cartDrawer', { ovId: 'cartOverlay', closeId: 'cartClose', onOpen: fetchCart });
  document.querySelectorAll('[data-cart-open]').forEach(b => b.addEventListener('click', () => openDrawer('cartDrawer')));
  document.getElementById('cartContinue')?.addEventListener('click', () => closeDrawer('cartDrawer'));
  fetchCart();
  const csClose = document.getElementById('cartCrossSellClose');
  if (csClose) {
    csClose.addEventListener('click', () => {
      const c = document.getElementById('cartCrossSell');
      if (c) c.style.display = 'none';
      sessionStorage.setItem('crossSellDismissed', '1');
    });
  }
}
async function fetchCart() {
  try {
    const res = await fetch('/cart.js');
    cartData = await res.json();
    renderCart(cartData);
  } catch (e) { console.warn('Cart fetch failed', e); }
}
function renderCart(cart) {
  updateCartBadge(cart.item_count);
  updateShipBar(cart.total_price / 100);
  const body = document.getElementById('cartBody');
  const footer = document.getElementById('cartFooter');
  const subRow = document.getElementById('cartSubRow');
  const superCount = document.getElementById('cartSuperCount');
  if (superCount) superCount.textContent = cart.item_count > 0 ? cart.item_count : '';
  if (subRow) {
    subRow.style.display = cart.item_count > 0 ? 'block' : 'none';
    const st = document.getElementById('cartSubtotal');
    if (st) st.textContent = money(cart.total_price);
  }
  if (footer) footer.style.display = cart.item_count > 0 ? 'block' : 'none';
  if (!body) return;
  if (cart.item_count === 0) {
    body.innerHTML = `<div class="cart-empty"><svg viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg><p class="cart-empty__text">Your cart is empty</p><a href="/collections/all" class="cart-empty__link" onclick="closeDrawer('cartDrawer')">Continue Shopping</a></div>`;
    return;
  }
  body.innerHTML = cart.items.map(item => `
    <div class="cart-item" data-key="${item.key}">
      <a href="${item.url}" class="cart-item__img" onclick="closeDrawer('cartDrawer')">
        ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
      </a>
      <div class="cart-item__details">
        <div class="cart-item__vendor">${item.vendor || ''}</div>
        <a href="${item.url}" class="cart-item__title" onclick="closeDrawer('cartDrawer')">${item.product_title}</a>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
        <div class="cart-item__row">
          <div class="qty-wrap">
            <button class="qty-btn" onclick="changeQty('${item.key}',${item.quantity - 1})" ${item.quantity <= 1 ? 'disabled' : ''}>−</button>
            <span class="qty-num">${item.quantity}</span>
            <button class="qty-btn" onclick="changeQty('${item.key}',${item.quantity + 1})">+</button>
          </div>
          <span class="cart-item__price">${money(item.line_price)}</span>
        </div>
        <button class="cart-item__remove" onclick="changeQty('${item.key}',0)">Remove</button>
      </div>
    </div>`).join('');

  // Render cross-sell (Complete The Look)
  renderCrossSell(cart);
}

async function renderCrossSell(cart) {
  const container = document.getElementById('cartCrossSell');
  const itemsWrap = document.getElementById('cartCrossSellItems');
  if (!container || !itemsWrap || cart.item_count === 0) {
    if (container) container.style.display = 'none';
    return;
  }

  if (sessionStorage.getItem('crossSellDismissed') === '1') {
    container.style.display = 'none';
    return;
  }

  const inCartIds = new Set(cart.items.map(i => i.product_id));
  const inCartHandles = new Set(cart.items.map(i => i.handle));

  // Gather styled-with handle lists from cart line item properties (in cart order)
  let styledHandles = [];
  for (const item of cart.items) {
    if (item.properties && item.properties._styled_handles) {
      const handles = item.properties._styled_handles.split(',').map(h => h.trim()).filter(Boolean);
      handles.forEach(h => { if (!styledHandles.includes(h)) styledHandles.push(h); });
    }
  }

  // Find the FIRST styled handle not already in the cart (sequential)
  let nextHandle = null;
  for (const h of styledHandles) {
    if (!inCartHandles.has(h)) { nextHandle = h; break; }
  }

  if (!nextHandle) {
    // No more styled products to suggest (all added, or none set)
    container.style.display = 'none';
    return;
  }

  // Fetch that product
  let product = null;
  try {
    const res = await fetch('/products/' + nextHandle + '.js');
    if (res.ok) {
      const p = await res.json();
      if (!inCartIds.has(p.id)) product = p;
    }
  } catch(e) {}

  if (!product) {
    container.style.display = 'none';
    return;
  }

  const variants = product.variants || [];
  const hasRealVariants = !(variants.length === 1 && variants[0].title === 'Default Title');
  const img = product.featured_image || (product.images && product.images[0]) || '';

  let addControl;
  if (hasRealVariants) {
    const opts = variants.map(v =>
      `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>${v.title}${!v.available ? ' — Sold Out' : ''}</option>`
    ).join('');
    addControl = `
      <div class="cart-crosssell__addwrap">
        <select class="cart-crosssell__size" id="csSizeSelect">
          <option value="">Size</option>
          ${opts}
        </select>
        <button class="cart-crosssell__add" onclick="crossSellAddSelected()">Add</button>
      </div>`;
  } else {
    const v = variants[0];
    addControl = `<button class="cart-crosssell__add" ${!v.available ? 'disabled' : ''} onclick="crossSellAdd(${v.id})">${v.available ? 'Add' : 'Sold Out'}</button>`;
  }

  itemsWrap.innerHTML = `
    <div class="cart-crosssell__item">
      <img class="cart-crosssell__img" src="${img}" alt="${product.title}" loading="lazy">
      <div class="cart-crosssell__info">
        <a href="${product.url}" class="cart-crosssell__name" onclick="closeDrawer('cartDrawer')">${product.title}</a>
        <span class="cart-crosssell__price">${money(product.price)}</span>
        ${addControl}
      </div>
    </div>`;

  container.style.display = 'block';
}

window.crossSellAddSelected = async function() {
  const sel = document.getElementById('csSizeSelect');
  if (!sel || !sel.value) {
    if (sel) { sel.style.borderColor = '#d9534f'; setTimeout(() => sel.style.borderColor = '', 1500); }
    return;
  }
  await addToCart(sel.value, 1);
};

window.crossSellAdd = async function(variantId) {
  if (!variantId) return;
  await addToCart(variantId, 1);
};
function updateCartBadge(n) {
  document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = n; el.style.display = n > 0 ? 'flex' : 'none'; });
}
function updateShipBar(total) {
  const FREE = (window.adSettings?.freeShipping) || 200;
  const fill = document.getElementById('shipFill');
  const text = document.getElementById('shipText');
  if (!fill || !text) return;
  const pct = Math.min(100, (total / FREE) * 100);
  fill.style.width = pct + '%';
  fill.classList.toggle('ship-bar__fill--done', total >= FREE);
  text.innerHTML = total >= FREE
    ? `🎉 You've unlocked <strong>FREE shipping!</strong>`
    : `You're <strong>${money((FREE - total) * 100)}</strong> away from <strong>free shipping</strong>`;
}
async function changeQty(key, qty) {
  try {
    const res = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) });
    const cart = await res.json();
    renderCart(cart);
  } catch (e) { console.warn(e); }
}
async function addToCart(variantId, qty = 1, properties = null) {
  try {
    const payload = { id: parseInt(variantId, 10), quantity: qty };
    if (properties && Object.keys(properties).length) payload.properties = properties;
    const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload) });
    if (!res.ok) { const err = await res.json(); throw new Error(err.description || 'Could not add to cart'); }
    const addedItem = await res.json();

    // Notify Shopify's Customer Events / analytics layer (feeds Meta, Google, Pinterest, Klaviyo)
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('product_added_to_cart', {
        cartLine: {
          quantity: addedItem.quantity,
          merchandise: {
            id: `gid://shopify/ProductVariant/${addedItem.variant_id}`,
            price: { amount: addedItem.price / 100, currencyCode: 'USD' },
            product: {
              title: addedItem.product_title,
              vendor: addedItem.vendor
            }
          }
        }
      });
    }

    await fetchCart();
    openDrawer('cartDrawer');
    toast('Added to bag ✓');
  } catch (e) { toast(e.message || 'Error adding to cart'); }
}
window.changeQty = changeQty;
window.addToCart = addToCart;
window.closeDrawer = closeDrawer;

/* ── Quick Add (collection cards) ── */
async function cardQuickAdd(btn, variantId) {
  if (btn.classList.contains('is-unavail')) return;
  if (btn.dataset.loading) return;
  btn.dataset.loading = '1';
  const orig = btn.textContent;
  btn.textContent = '…';
  const card = btn.closest('.prod-card');
  const styled = card && card.dataset.styled ? card.dataset.styled : '';
  const payload = { id: parseInt(variantId, 10), quantity: 1 };
  if (styled) payload.properties = { _styled_handles: styled };
  try {
    const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload) });
    if (!res.ok) { const err = await res.json(); throw new Error(err.description || 'Error'); }
    const addedItem = await res.json();

    // Notify Shopify's Customer Events / analytics layer
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('product_added_to_cart', {
        cartLine: {
          quantity: addedItem.quantity,
          merchandise: {
            id: `gid://shopify/ProductVariant/${addedItem.variant_id}`,
            price: { amount: addedItem.price / 100, currencyCode: 'USD' },
            product: {
              title: addedItem.product_title,
              vendor: addedItem.vendor
            }
          }
        }
      });
    }

    btn.classList.add('is-added'); btn.textContent = '✓';
    await fetchCart();
    openDrawer('cartDrawer');
    setTimeout(() => { btn.classList.remove('is-added'); btn.textContent = orig; delete btn.dataset.loading; }, 2200);
  } catch (e) { btn.textContent = orig; delete btn.dataset.loading; toast(e.message || 'Error'); }
}
window.cardQuickAdd = cardQuickAdd;

/* ── Notifications (blog posts) ── */
let notifPosts = [];
function initNotif() {
  registerDrawer('notifDrawer', { ovId: 'notifOverlay', triggerId: 'notifBtn', closeId: 'notifClose', onOpen: loadNotif });
  document.getElementById('notifBackBtn')?.addEventListener('click', showNotifList);
}
async function loadNotif() {
  if (notifPosts.length) { showNotifList(); return; }
  const listEl = document.getElementById('notifList');
  if (listEl) listEl.innerHTML = '<p style="padding:24px;font-family:var(--font-cond);font-size:12px;letter-spacing:.1em;text-transform:uppercase;color:var(--text3);text-align:center">Loading…</p>';
  try {
    let res = await fetch('/blogs/news.json?limit=8');
    let data = await res.json();
    if (!data.articles?.length) { res = await fetch('/pages/lookbooks.json?limit=8'); data = await res.json(); }
    notifPosts = data.articles || [];
  } catch (e) { notifPosts = []; }
  const dot = document.getElementById('notifBadge');
  if (dot && notifPosts.length) dot.style.display = 'block';
  showNotifList();
}
function showNotifList() {
  const list = document.getElementById('notifList');
  const detail = document.getElementById('notifDetail');
  const back = document.getElementById('notifBackBtn');
  const title = document.getElementById('notifTitle');
  if (!list) return;
  list.style.display = 'block';
  if (detail) detail.style.display = 'none';
  if (back) back.style.display = 'none';
  if (title) title.textContent = 'NOTIFICATIONS';
  if (!notifPosts.length) { list.innerHTML = '<p style="padding:40px 18px;font-family:var(--font-cond);font-size:12px;letter-spacing:.1em;text-transform:uppercase;color:var(--text3);text-align:center">No notifications</p>'; return; }
  list.innerHTML = notifPosts.map((p, i) => `
    <div class="notif-item" onclick="showNotifDetail(${i})" role="button" tabindex="0" onkeydown="if(event.key==='Enter')showNotifDetail(${i})">
      <div class="notif-item__thumb">${p.image ? `<img src="${p.image.src}" alt="${p.title}" loading="lazy">` : ''}</div>
      <div>
        <div class="notif-item__title">${p.title.toUpperCase()}</div>
        <div class="notif-item__excerpt">${(p.summary_html || p.body_html || '').replace(/<[^>]*>/g,'').slice(0,90)}…</div>
      </div>
      <div class="notif-item__arrow"><svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg></div>
    </div>`).join('');
}
function showNotifDetail(i) {
  const p = notifPosts[i]; if (!p) return;
  const list = document.getElementById('notifList');
  const detail = document.getElementById('notifDetail');
  const back = document.getElementById('notifBackBtn');
  const title = document.getElementById('notifTitle');
  if (list) list.style.display = 'none';
  if (detail) {
    detail.style.display = 'block';
    const bodyText = (p.body_html || '').replace(/<[^>]*>/g,' ').replace(/\s+/g,' ').trim().slice(0,300);
    detail.innerHTML = `
      <div class="notif-detail__img">${p.image ? `<img src="${p.image.src}" alt="${p.title}" loading="lazy">` : ''}</div>
      <div class="notif-detail__body">
        <h3 class="notif-detail__title">${p.title.toUpperCase()}</h3>
        <p class="notif-detail__text">${bodyText}${bodyText.length >= 300 ? '…' : ''}</p>
        <a href="/blogs/news/${p.handle}" class="notif-detail__btn" onclick="closeDrawer('notifDrawer')">Shop Now</a>
      </div>`;
  }
  if (back) back.style.display = 'flex';
  if (title) title.textContent = '';
}
window.showNotifDetail = showNotifDetail;
window.showNotifList = showNotifList;

/* ── Watchlist ── */
const WL_KEY = 'ad-watchlist';
const WL_EMAIL_KEY = 'ad-wl-email';
function getWL() { try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch { return []; } }
function saveWL(list) { localStorage.setItem(WL_KEY, JSON.stringify(list)); updateWLBadge(); }
function updateWLBadge() {
  const n = getWL().length;
  const badge = document.getElementById('wlBadge');
  if (badge) { badge.textContent = n; badge.style.display = n > 0 ? 'flex' : 'none'; }
}
function toggleWL(id, title, price, img, url) {
  const list = getWL();
  const idx = list.findIndex(p => p.id == id);
  if (idx > -1) { list.splice(idx, 1); saveWL(list); toast('Removed from Watchlist'); return false; }
  list.push({ id, title, price, img, url, at: Date.now() });
  saveWL(list);
  toast('Added to Watchlist ✓');
  return true;
}
function initWatchlist() {
  updateWLBadge();
  registerDrawer('watchlistDrawer', { ovId: 'wlOverlay', triggerId: 'wlBtn', closeId: 'wlClose', onOpen: renderWL });
  document.querySelectorAll('[data-wl-btn]').forEach(btn => {
    const pid = btn.dataset.wlBtn;
    if (getWL().find(p => p.id == pid)) { btn.classList.add('is-wished'); btn.querySelector('svg')?.setAttribute('fill','currentColor'); }
    btn.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      const card = btn.closest('[data-product-card]') || btn.closest('.pdp-info');
      const title = card?.querySelector('[data-card-title]')?.textContent || document.title;
      const price = card?.querySelector('[data-card-price]')?.textContent || '';
      const img   = card?.querySelector('img')?.src || '';
      const url   = card?.querySelector('a')?.href || window.location.pathname;
      const wished = toggleWL(pid, title, price, img, url);
      btn.classList.toggle('is-wished', wished);
      btn.querySelector('svg')?.setAttribute('fill', wished ? 'currentColor' : 'none');
    });
  });
}
function renderWL() {
  const list = getWL();
  const items = document.getElementById('wlItems');
  const empty = document.getElementById('wlEmpty');
  const footer = document.getElementById('wlFooter');
  const viewAllWrap = document.getElementById('wlViewAll');
  if (!items) return;
  if (list.length === 0) {
    items.innerHTML = '';
    if (empty) empty.style.display = 'flex';
    if (footer) footer.style.display = 'none';
    if (viewAllWrap) viewAllWrap.style.display = 'none';
    return;
  }
  if (empty) empty.style.display = 'none';
  if (footer) footer.style.display = 'block';
  if (viewAllWrap) viewAllWrap.style.display = 'block';
  items.innerHTML = list.map(p => `
    <div class="wl-item">
      <a href="${p.url}" class="wl-item__img" onclick="closeDrawer('watchlistDrawer')">
        ${p.img ? `<img src="${p.img}" alt="${p.title}" loading="lazy">` : '<div style="width:100%;height:100%;background:var(--bg3)"></div>'}
      </a>
      <div>
        <a href="${p.url}" class="wl-item__name" onclick="closeDrawer('watchlistDrawer')">${p.title}</a>
        <div class="wl-item__price">${p.price}</div>
        <span class="wl-item__tag">Watching</span>
        <br>
        <button class="wl-item__remove" onclick="removeWL('${p.id}')">Remove</button>
      </div>
    </div>`).join('');
  const emailInput = document.getElementById('wlEmail');
  const saved = localStorage.getItem(WL_EMAIL_KEY);
  if (emailInput && saved) emailInput.value = saved;
}
function removeWL(id) {
  const list = getWL().filter(p => p.id != id); saveWL(list); renderWL();
  document.querySelectorAll(`[data-wl-btn="${id}"]`).forEach(b => { b.classList.remove('is-wished'); b.querySelector('svg')?.setAttribute('fill','none'); });
}
window.removeWL = removeWL;
window.saveWLEmail = function() {
  const v = document.getElementById('wlEmail')?.value?.trim();
  if (!v || !v.includes('@')) { toast('Enter a valid email'); return; }
  localStorage.setItem(WL_EMAIL_KEY, v); toast('You\'ll be notified at ' + v + ' ✓');
};

/* ── Account Drawer ── */
function initAccount() {
  registerDrawer('accountDrawer', { ovId: 'accountOverlay', triggerId: 'accountBtn', closeId: 'accountClose' });
}

/* ── Product Page ── */
function initPDP() {
  // Gallery
  const imgUrls = window.adPdpImages || [];
  let current = 0;
  const mainImg = document.getElementById('pdpMainImg');
  const counter = document.getElementById('pdpCounter');
  function goTo(i) {
    if (!imgUrls.length || i < 0 || i >= imgUrls.length) return;
    current = i;
    if (mainImg) { mainImg.src = imgUrls[i].src; mainImg.srcset = imgUrls[i].srcset || ''; }
    if (counter) counter.textContent = `${i + 1} / ${imgUrls.length}`;
    document.querySelectorAll('.pdp-thumb').forEach((t, ti) => t.classList.toggle('is-active', ti === i));
  }
  document.getElementById('galleryPrev')?.addEventListener('click', () => goTo(current > 0 ? current - 1 : imgUrls.length - 1));
  document.getElementById('galleryNext')?.addEventListener('click', () => goTo(current < imgUrls.length - 1 ? current + 1 : 0));
  document.querySelectorAll('.pdp-thumb').forEach((t, i) => t.addEventListener('click', () => goTo(i)));
  // Swipe
  const gallery = document.getElementById('pdpGalleryMain');
  if (gallery) {
    let sx = 0;
    gallery.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
    gallery.addEventListener('touchend', e => { const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 40) goTo(dx < 0 ? Math.min(current + 1, imgUrls.length - 1) : Math.max(current - 1, 0)); }, { passive: true });
  }

  // Size selector
  const varInput = document.getElementById('variantInput');
  const atcBtn = document.getElementById('mainAtcBtn');
  const priceEl = document.getElementById('pdpPrice');
  const stickyPrice = document.querySelector('.pdp-sticky-bar__price');
  document.querySelectorAll('[data-size-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('[data-size-btn]').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      if (varInput) varInput.value = btn.dataset.variantId;
      const avail = btn.dataset.available !== 'false';
      if (atcBtn) { atcBtn.disabled = !avail; atcBtn.textContent = avail ? 'Add to Cart' : 'Sold Out'; }
      const p = parseInt(btn.dataset.price || 0);
      if (p > 0) {
        const pm = money(p);
        if (priceEl) priceEl.textContent = pm;
        if (stickyPrice) stickyPrice.textContent = pm;
      }
      // Update stock dot
      const dot = document.getElementById('pdpStockDot');
      const lbl = document.getElementById('pdpStockLabel');
      if (dot) dot.classList.toggle('pdp-stock-dot--out', !avail);
      if (lbl) lbl.textContent = avail ? 'IN STOCK' : 'OUT OF STOCK';
      // Low stock urgency message
      const lowMsg = document.getElementById('lowStockMsg');
      if (lowMsg) {
        const isLow = btn.dataset.low === 'true';
        const qty = parseInt(btn.dataset.inventory || '0');
        const sizeLabel = btn.dataset.sizeLabel || '';
        if (isLow && qty > 0) {
          lowMsg.textContent = 'Only ' + qty + ' left in ' + sizeLabel;
          lowMsg.classList.add('is-visible');
        } else {
          lowMsg.textContent = '';
          lowMsg.classList.remove('is-visible');
        }
      }
    });
  });

  // Show low-stock message on load if the pre-selected size is low
  (function() {
    const active = document.querySelector('[data-size-btn].is-active');
    const lowMsg = document.getElementById('lowStockMsg');
    if (active && lowMsg && active.dataset.low === 'true') {
      const qty = parseInt(active.dataset.inventory || '0');
      if (qty > 0) {
        lowMsg.textContent = 'Only ' + qty + ' left in ' + (active.dataset.sizeLabel || '');
        lowMsg.classList.add('is-visible');
      }
    }
  })();

  // Colour selector
  document.querySelectorAll('[data-colour-swatch]').forEach(s => {
    s.addEventListener('click', () => {
      document.querySelectorAll('[data-colour-swatch]').forEach(x => x.classList.remove('is-active'));
      s.classList.add('is-active');
      const lbl = document.getElementById('selectedColour');
      if (lbl) lbl.textContent = s.dataset.colour;
      if (mainImg && s.dataset.imgSrc) { mainImg.src = s.dataset.imgSrc; if (mainImg.srcset !== undefined) mainImg.srcset = s.dataset.imgSrcset || ''; counter && (counter.textContent = '1 / ' + imgUrls.length); document.querySelectorAll('.pdp-thumb').forEach(t => t.classList.remove('is-active')); document.querySelector('.pdp-thumb')?.classList.add('is-active'); current = 0; }
    });
  });

  // Product form submit
  document.getElementById('pdpForm')?.addEventListener('submit', async e => {
    e.preventDefault();
    if (!varInput?.value || varInput.value === '0') { toast('Please select a size'); return; }
    if (atcBtn) atcBtn.disabled = true;
    // Collect line item properties (e.g. _styled_handle)
    const props = {};
    document.querySelectorAll('#pdpForm input[name^="properties["]').forEach(inp => {
      const m = inp.name.match(/properties\[(.+?)\]/);
      if (m && inp.value) props[m[1]] = inp.value;
    });
    await addToCart(varInput.value, 1, props);
    if (atcBtn) { atcBtn.disabled = false; }
  });

  // Accordion
  document.querySelectorAll('[data-accordion-trigger]').forEach(btn => {
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', !open);
      const body = btn.nextElementSibling;
      if (body) body.style.display = open ? 'none' : 'block';
    });
  });

  // Size guide unit toggle (inches / cm)
  document.querySelectorAll('[data-sg-unit]').forEach(btn => {
    btn.addEventListener('click', () => {
      const unit = btn.dataset.sgUnit;
      const sg = btn.closest('.sg');
      if (!sg) return;
      sg.querySelectorAll('[data-sg-unit]').forEach(b => {
        const active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      sg.querySelectorAll('.sg__table td[data-cm]').forEach(td => {
        td.textContent = unit === 'cm' ? td.dataset.cm : td.dataset.in;
      });
    });
  });

  // Sticky bar
  const infoEl = document.getElementById('pdpInfoPanel');
  const stickyBar = document.getElementById('pdpStickyBar');
  if (infoEl && stickyBar) {
    const io = new IntersectionObserver(([e]) => { stickyBar.classList.toggle('is-visible', !e.isIntersecting); }, { threshold: 0, rootMargin: '-60px 0px 0px 0px' });
    io.observe(infoEl);
  }

  // Strip drag / scroll indicator
  const track = document.getElementById('pdpStripTrack');
  const thumb = document.getElementById('pdpStripThumb');
  if (track && thumb) {
    function updateThumb() {
      const pct = track.scrollLeft / (track.scrollWidth - track.clientWidth) || 0;
      const trackW = thumb.parentElement?.clientWidth || 200;
      const thumbW = thumb.offsetWidth || 60;
      thumb.style.transform = `translateX(${pct * (trackW - thumbW)}px)`;
    }
    track.addEventListener('scroll', updateThumb, { passive: true });
    let isDrag = false, startX = 0, startScroll = 0;
    track.addEventListener('mousedown', e => { isDrag = true; startX = e.pageX; startScroll = track.scrollLeft; track.style.cursor = 'grabbing'; });
    document.addEventListener('mousemove', e => { if (isDrag) track.scrollLeft = startScroll - (e.pageX - startX); });
    document.addEventListener('mouseup', () => { isDrag = false; track.style.cursor = ''; });

    // Auto-scroll the strip on desktop
    if (window.innerWidth >= 900) {
      let autoScroll;
      let paused = false;
      const items = track.querySelectorAll('.pdp-strip__item');
      let currentItem = 0;

      function scrollToItem(idx) {
        if (!items.length) return;
        currentItem = idx % items.length;
        const item = items[currentItem];
        if (item) {
          track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
        }
      }

      function startAutoScroll() {
        clearInterval(autoScroll);
        autoScroll = setInterval(() => {
          if (paused) return;
          // Loop back to start if near the end
          if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 10) {
            currentItem = 0;
            track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollToItem(currentItem + 1);
          }
        }, 3000);
      }

      // Pause on hover/interaction
      track.addEventListener('mouseenter', () => { paused = true; });
      track.addEventListener('mouseleave', () => { paused = false; });
      track.addEventListener('touchstart', () => { paused = true; }, { passive: true });

      startAutoScroll();
    }
  }

  // Recently viewed
  try {
    const pid = window.adCurrentProduct?.id;
    if (pid) {
      let rv = JSON.parse(localStorage.getItem('ad-rv') || '[]');
      rv = rv.filter(p => p.id !== pid);
      rv.unshift(window.adCurrentProduct);
      if (rv.length > 8) rv = rv.slice(0, 8);
      localStorage.setItem('ad-rv', JSON.stringify(rv));
      const others = rv.filter(p => p.id !== pid).slice(0, 4);
      const rvSection = document.getElementById('rvSection');
      const rvGrid = document.getElementById('rvGrid');
      if (others.length && rvSection && rvGrid) {
        rvSection.style.display = 'block';
        rvGrid.innerHTML = others.map(p => `<a href="${p.url}" class="pdp-rec-card"><div class="pdp-rec-card__img"><img src="${p.img}" alt="${p.title}" loading="lazy"></div><div class="pdp-rec-card__info"><span class="pdp-rec-card__title">${p.title}</span><span class="pdp-rec-card__price">${p.price}</span></div></a>`).join('');
      }
    }
  } catch (e) {}

  // Watchlist on PDP
  document.querySelectorAll('[data-wl-btn]').forEach(btn => {
    const pid = btn.dataset.wlBtn;
    if (getWL().find(p => p.id == pid)) { btn.classList.add('is-wished'); btn.querySelector('svg')?.setAttribute('fill', 'currentColor'); }
    btn.addEventListener('click', e => {
      e.preventDefault();
      const cp = window.adCurrentProduct || {};
      const wished = toggleWL(pid, cp.title || '', cp.price || '', cp.img || '', cp.url || window.location.pathname);
      btn.classList.toggle('is-wished', wished);
      btn.querySelector('svg')?.setAttribute('fill', wished ? 'currentColor' : 'none');
    });
  });
}

/* ── Collection page ── */
function initCollection() {
  const grid = document.getElementById('collGrid');
  function setView(v) {
    if (!grid) return;
    grid.dataset.view = v;
    document.getElementById('viewProduct')?.classList.toggle('is-active', v === 'product');
    document.getElementById('viewModel')?.classList.toggle('is-active', v === 'model');
    const sw = document.getElementById('modelSw');
    sw?.setAttribute('aria-pressed', v === 'model' ? 'true' : 'false');
  }
  document.getElementById('viewProduct')?.addEventListener('click', () => setView('product'));
  document.getElementById('viewModel')?.addEventListener('click', () => setView('model'));
  document.getElementById('modelSw')?.addEventListener('click', () => setView(grid?.dataset.view === 'product' ? 'model' : 'product'));
  const fp = document.getElementById('filterPanel'), fl = document.getElementById('collLayout');
  document.getElementById('filterBtn')?.addEventListener('click', () => { if (fp?.hasAttribute('hidden')) { fp.removeAttribute('hidden'); fl?.classList.add('filter-open'); } else { fp.setAttribute('hidden',''); fl?.classList.remove('filter-open'); } });
  document.getElementById('filterClose')?.addEventListener('click', () => { fp?.setAttribute('hidden',''); fl?.classList.remove('filter-open'); });
  document.querySelectorAll('.filter-group__toggle').forEach(b => {
    b.addEventListener('click', () => { const exp = b.getAttribute('aria-expanded') === 'true'; b.setAttribute('aria-expanded', !exp); const body = b.nextElementSibling; if (body) body.style.display = exp ? 'none' : ''; });
  });
  document.addEventListener('click', e => { if (!e.target.closest('.prod-card')) document.querySelectorAll('.prod-card.sizes-open').forEach(c => c.classList.remove('sizes-open')); });
}

/* ── Size Guide Modal ── */
function initSizeGuide() {
  const ov = document.getElementById('sgOverlay');
  document.querySelectorAll('[data-size-guide]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); ov?.classList.add('is-open'); }));
  document.getElementById('sgClose')?.addEventListener('click', () => ov?.classList.remove('is-open'));
  ov?.addEventListener('click', e => { if (e.target === ov) ov.classList.remove('is-open'); });
  document.querySelectorAll('.tab-btn').forEach(b => {
    b.addEventListener('click', () => {
      b.closest('.tab-nav')?.querySelectorAll('.tab-btn').forEach(x => x.classList.remove('is-active')); b.classList.add('is-active');
      const panel = document.getElementById(b.dataset.tab); document.querySelectorAll('.tab-panel').forEach(p => p.classList.remove('is-active')); panel?.classList.add('is-active');
    });
  });
}

/* ── INIT ── */
document.addEventListener('DOMContentLoaded', () => {
  initDark();
  initHeader();
  initMobileNav();
  initSidebars();
  initCart();
  initNotif();
  initWatchlist();
  initAccount();
  if (document.body.classList.contains('template-product')) initPDP();
  if (document.body.classList.contains('template-collection')) initCollection();
  initSizeGuide();
});
